import React from 'react'
// c:\xampp\htdocs\miners-market-main\components\QuoteRequestsManager.js
import Pagination from './Pagination.jsx';
import BasketQuoteItemsModal from './BasketQuoteItemsModal.jsx';
import ConfirmationModal from './ConfirmationModal.jsx';
import { openExternalLink } from '../utils/shared.js';

export default function QuoteRequestsManager({ onViewProductById }) {
    const [requests, setRequests] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [showBasketModal, setShowBasketModal] = React.useState(false); // State for basket items modal
    const [basketItems, setBasketItems] = React.useState([]); // State for basket items
    const [loadingBasketItems, setLoadingBasketItems] = React.useState(false); // State for loading basket items
    const [showDeleteModal, setShowDeleteModal] = React.useState(false); // State for delete confirmation modal
    const [itemToDelete, setItemToDelete] = React.useState(null); // State for item to delete
    const itemsPerPage = 15;
    
    // Helper to check if a date is within the last 24 hours
    const isRecent = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        return (now - date) < (24 * 60 * 60 * 1000);
    };

    React.useEffect(() => {
        const fetchRequests = async () => {
            try {
                setLoading(true);
                const response = await fetch('/api/admin_get_quote_requests.php', {
                    credentials: 'include'
                });
                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || 'Failed to fetch quote requests.');
                }
                const data = await response.json();

                // Sort by the most recent request date
                data.sort((a, b) => {
                    const dateA = new Date(a.request_date).getTime();
                    const dateB = new Date(b.request_date).getTime();
                    return dateB - dateA; // Sort newest to oldest
                });
                setRequests(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    const filteredRequests = React.useMemo(() => {
        if (!searchTerm) return requests;
        const lowercasedFilter = searchTerm.toLowerCase();
        return requests.filter(req =>
            req.buyer_name.toLowerCase().includes(lowercasedFilter) ||
            req.buyer_email.toLowerCase().includes(lowercasedFilter) ||
            req.product_name.toLowerCase().includes(lowercasedFilter) ||
            req.listing_number.toLowerCase().includes(lowercasedFilter)
        );
    }, [requests, searchTerm]);

    const handleViewQuote = (req) => {
        const url = `/api/view_quote.php?product_id=${req.product_id}&listing_type=${req.listing_type}`;
        openExternalLink(url);
    };

    const handleViewProduct = async (productId, listingType, requestId) => {
        // Basket quotes don't have single products - show modal with items
        if (listingType === 'basket' || productId === 0) {
            setShowBasketModal(true);
            setLoadingBasketItems(true);
            try {
                const response = await fetch(`/api/get_basket_quote_items.php?quote_request_id=${requestId}`, { credentials: 'include' });
                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || 'Failed to fetch basket items.');
                }
                const items = await response.json();
                setBasketItems(items);
            } catch (error) {
                console.error('Error fetching basket items:', error);
                alert(`Error loading basket items: ${error.message}`);
                setShowBasketModal(false);
            } finally {
                setLoadingBasketItems(false);
            }
            return;
        }
        onViewProductById(productId, listingType);
    };

    const handleDeleteQuote = async (req) => {
        setItemToDelete(req);
        setShowDeleteModal(true);
    };

    const confirmDeleteQuote = async () => {
        if (!itemToDelete) return;
        
        try {
            const response = await fetch('/api/delete_quote_request.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ request_id: itemToDelete.request_id })
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to delete quote request.');
            }
            setRequests(requests.filter(r => r.request_id !== itemToDelete.request_id));
        } catch (error) {
            console.error('Error deleting quote request:', error);
            alert('Failed to delete quote request');
        } finally {
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    // --- Pagination Logic ---
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const currentRequests = filteredRequests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
            <div className="flex flex-col md:flex-row justify-between md:items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">Quote Requests</h2>
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search by buyer, product, or listing #"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-80"
                    />
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                </div>
            </div>

            {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>}
            {error && <div className="text-center py-10 text-red-500">Error: {error}</div>}

            {!loading && !error && (
                <>
                    <div className="space-y-4">
                        {/* Desktop Headers */}
                        <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase">
                            <div className="md:col-span-3">Product</div>
                            <div className="md:col-span-3">Buyer</div>
                            <div className="md:col-span-2">Seller</div>
                            <div className="md:col-span-2">Date</div>
                            <div className="md:col-span-2 text-right">Actions</div>
                        </div>
                        {/* Requests List */}
                        {currentRequests.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">No quote requests found.</div>
                        ) : (
                            currentRequests.map(req => (
                                <div key={req.request_id} className="bg-white border rounded-lg p-4 grid grid-cols-2 gap-y-2 md:grid-cols-12 md:gap-4 md:p-0 md:py-4 md:border-0 md:border-b items-center">
                                    {/* Product */}
                                    <div className="col-span-2 md:col-span-3">
                                        <div className="text-xs text-gray-500 md:hidden">Product</div>
                                        <div className="text-sm font-medium text-gray-900">{req.product_name}</div>
                                        <div className="text-sm text-gray-500 font-mono">{req.listing_number}</div>
                                        {isRecent(req.request_date) && (
                                            <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse ml-2">New</span>
                                        )}
                                    </div>
                                    {/* Buyer */}
                                    <div className="col-span-2 md:col-span-3">
                                        <div className="text-xs text-gray-500 md:hidden">Buyer</div>
                                        <div className="text-sm font-medium text-gray-900">{req.buyer_name}</div>
                                        <div className="text-sm text-gray-500 break-all">{req.buyer_email}</div>
                                    </div>
                                    {/* Seller */}
                                    <div className="col-span-1 md:col-span-2">
                                        <div className="text-xs text-gray-500 md:hidden">Seller</div>
                                        <div className="text-sm text-gray-900">{req.seller_name}</div>
                                    </div>
                                    {/* Date */}
                                    <div className="col-span-1 md:col-span-2">
                                        <div className="text-xs text-gray-500 md:hidden">Date</div>
                                        <div className="text-sm text-gray-500">{new Date(req.request_date).toLocaleString()}</div>
                                    </div>
                                    {/* Actions */}
                                    <div className="col-span-2 md:col-span-2 text-right space-x-4">
                                        <button 
                                            onClick={() => handleViewProduct(req.product_id, req.listing_type, req.request_id)} 
                                            className="text-blue-600 hover:text-blue-900"
                                        >
                                            View Item
                                        </button>
                                        <button onClick={() => handleViewQuote(req)} className="text-green-600 hover:text-green-900">View Quote</button>
                                        <button onClick={() => handleDeleteQuote(req)} className="text-red-600 hover:text-red-900">Delete</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </>
            )}

            {/* Basket Quote Items Modal */}
            <BasketQuoteItemsModal
                isOpen={showBasketModal}
                onClose={() => setShowBasketModal(false)}
                items={basketItems}
                onViewProduct={onViewProductById}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteQuote}
                title="Delete Quote Request"
                message={`Are you sure you want to delete this quote request for "${itemToDelete?.product_name}" from ${itemToDelete?.buyer_name}?`}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
        </div>
    );
}
